import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { JANE_DEFINED_ROWS } from '@iheartjane/menu';
import { Flex, Link, Typography } from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import type { Nullish } from '@iheartjane/types';
import capitalize from 'lodash-es/capitalize';

import { useStoreMenuMetadata } from '@jane/business-admin/data-access';
import { useDebouncedTrack, useStoreId } from '@jane/business-admin/hooks';
import type { MenuRow } from '@jane/business-admin/types';
import {
  EventNames,
  ModalNames,
  SettingNames,
} from '@jane/business-admin/util';

import { DynamicRowCard } from './DynamicRowCard';
import type { FormValues } from './form.types';
import { MenuRowCard } from './MenuRowCard';
import { OptionCardWithPill } from './OptionCardWithPill';

export const DetailsCard = ({
  defaultRowType,
  isCreateMode,
  row,
}: {
  defaultRowType?: string;
  isCreateMode: boolean;
  row?: MenuRow;
}) => {
  const newProductsRowEnabled = useFlag(FLAGS.c3NewProductsMenuRowTemplate);
  const posTagsRowEnabled = useFlag(FLAGS.c3PosTagsMenuRowTemplate);
  const {
    setValue,
    watch,
    formState: { isDirty },
  } = useFormContext<FormValues>();
  const storeId = useStoreId();
  const { data: menuMetadata } = useStoreMenuMetadata(storeId);
  const menuRowTypeValue = watch('menu_row_type');
  const customDisplayName = watch('custom_display_name');
  const trackTextChange = useDebouncedTrack(1000);
  const modalName = isCreateMode
    ? ModalNames.CreateMenuRow
    : ModalNames.EditMenuRow;

  const editableNameRowTypes = ['specials', 'sale', 'best_selling'];
  const pillOptions = [
    {
      menuRowType: 'custom' as const,
      title: 'Custom',
      subtitle: 'Individually choose products.',
      visible: true,
    },
    {
      menuRowType: 'new_products' as const,
      title: 'New products',
      subtitle: 'Products that have never been seen on your menu.',
      visible: newProductsRowEnabled,
      inUse: (menuMetadata?.row_count_meta?.new_products || 0) > 0,
    },
    {
      menuRowType: 'pos_tags' as const,
      title: 'POS product tags',
      subtitle: 'Automatically add products that have been tagged in your POS.',
      visible:
        posTagsRowEnabled && !!menuMetadata?.row_count_meta?.has_pos_tags,
    },
  ];

  return (
    <MenuRowCard>
      <Flex flexDirection="column">
        <Typography variant="h2-semibold" branded mb={24}>
          Details
        </Typography>

        <Typography variant="body-semibold" mb={16}>
          {isCreateMode ? 'Select r' : 'R'}ow type
        </Typography>

        {defaultRowType && JANE_DEFINED_ROWS.includes(defaultRowType) ? (
          <DynamicRowCard row_type={defaultRowType} />
        ) : (
          <Flex gap={20} mb={24}>
            {pillOptions
              .filter((opt) => opt.visible)
              .map((opt) => (
                <OptionCardWithPill
                  key={opt.menuRowType}
                  isCreateMode={isCreateMode}
                  {...opt}
                />
              ))}
          </Flex>
        )}

        <Form.TextField
          label="Row name"
          name="custom_display_name"
          required
          onChange={(value) => {
            const isRevert =
              value === row?.custom_display_name ||
              value === defaultRowType ||
              value === capitalize(defaultRowType);
            const valueNotChanged = !value || isRevert;

            if (!isDirty && valueNotChanged) return;

            trackTextChange({
              event: EventNames.ModifiedSetting,
              modal_name: modalName,
              revert: isRevert,
              setting_name: SettingNames.MenuRowDisplayName,
            });
          }}
        />
        {(() => {
          const showRevertLink =
            (menuRowTypeValue &&
              editableNameRowTypes.includes(menuRowTypeValue)) ||
            (defaultRowType && editableNameRowTypes.includes(defaultRowType));

          const isSpecialsOrDeals = (name: Nullish<string>) => {
            if (!name) return false;
            const nameLower = name.toLowerCase();
            return nameLower === 'specials' || nameLower === 'deals';
          };

          const getDefaultDisplayName = () => {
            if (
              defaultRowType &&
              editableNameRowTypes.includes(defaultRowType)
            ) {
              if (
                defaultRowType === 'specials' &&
                !isSpecialsOrDeals(customDisplayName)
              ) {
                return 'Deals';
              }
              return capitalize(defaultRowType);
            }
            return defaultRowType;
          };

          const shouldShowRevert = () => {
            if (!showRevertLink || !customDisplayName) return false;

            if (defaultRowType === 'specials') {
              return !isSpecialsOrDeals(customDisplayName);
            }

            return (
              customDisplayName !== defaultRowType &&
              customDisplayName !== capitalize(defaultRowType)
            );
          };

          return (
            shouldShowRevert() && (
              <Flex flexDirection="column" mt={24}>
                <Typography>Default: {getDefaultDisplayName()}</Typography>
                <Flex mt={4} mb={8}>
                  <Link
                    onClick={() =>
                      setValue('custom_display_name', getDefaultDisplayName())
                    }
                  >
                    Revert to default
                  </Link>
                </Flex>
              </Flex>
            )
          );
        })()}
      </Flex>
    </MenuRowCard>
  );
};
